import { redact } from './redact';

/**
 * Configuração partilhada do Sentry.
 *
 * O tipo é declarado localmente em vez de importado do SDK: este módulo é consumido por `apps/app`,
 * `apps/admin` e pelas Edge Functions (Deno), que não partilham a mesma instalação. A forma abaixo é
 * o subconjunto que o `beforeSend` toca.
 */
export interface SentryEventLike {
  message?: string;
  request?: { data?: unknown };
  extra?: unknown;
  contexts?: unknown;
  user?: { id?: string; [k: string]: unknown };
}

/**
 * Remove PII antes de o evento sair do dispositivo.
 *
 * O Sentry captura o contexto inteiro por padrão. Sem esta função, um produto que guarda data de
 * nascimento, NIF e IBAN transformaria a ferramenta de erro num repositório secundário de dados
 * pessoais — fora do RoPA, sem base legal e sem retenção definida. Ver `redact.ts`.
 *
 * Muta e devolve o próprio evento: o contrato do SDK espera-o de volta, e clonar perderia campos
 * internos.
 */
export function beforeSend(event: SentryEventLike): SentryEventLike {
  redact(event.request?.data);
  redact(event.extra);
  redact(event.contexts);

  if (event.user) {
    // Só o id sobrevive. `ip_address`, que o SDK preenche sozinho, é dado pessoal sob o RGPD.
    // Sem id, o objeto não serve para depurar e é removido por inteiro.
    event.user = event.user.id ? { id: event.user.id } : undefined;
  }

  return event;
}

/** Opções comuns aos três consumidores. O DSN e o ambiente vêm de quem inicializa. */
export const SENTRY_BASE_OPTIONS = {
  beforeSend,
  // `RNF` de erro: taxa <0,5%. Amostragem total no M0 para não perder sinal enquanto o volume é
  // baixo; reavaliar quando a franquia do tier gratuito (≈5 000 eventos/mês) apertar.
  tracesSampleRate: 1,
  // O SDK envia PII (IP, cookies) por padrão. Desligar na origem é mais seguro do que confiar
  // apenas no `beforeSend`: são duas camadas para a mesma falha.
  sendDefaultPii: false,
} as const;
